import type { IModelItem } from '@ardoq/yfiles';
import type { GraphItem } from '@ardoq/graph';

/**
 * Type guard that filters out null values from key-value pairs.
 * Used to remove failed item creations from the diff update process.
 */
export const filterNullValues = <TKey, TValue>(
  keyValuePair: [TKey, TValue | null]
): keyValuePair is [TKey, TValue] => keyValuePair[1] !== null;

export type DiffUpdateGraphArgs<T extends GraphItem, Y extends IModelItem> = {
  /**
   * The existing map of graph items, keyed by business object ID.
   * Items that are no longer present in businessObjects will be removed.
   */
  oldMap: Map<string, Y>;
  /**
   * The new business objects to sync to the graph.
   */
  businessObjects: T[];
  /**
   * Function to create a new graph item from a business object.
   * Return null if creation fails or should be skipped.
   */
  create: (graphItem: T) => Y | null;
  /**
   * Function to update an existing graph item from a business object.
   * Return null if update fails or should result in removal.
   */
  update: (graphItem: T) => Y | null;
  /**
   * Function to remove a graph item. Called for items that are no longer in businessObjects.
   */
  remove: (graphItem: Y, oldMap: Map<string, Y>) => void;
};

/**
 * Performs a differential update of graph items based on business objects.
 *
 * This is the core algorithm for syncing yFiles graph items (nodes, edges, groups)
 * with their corresponding business model objects (GraphNode, GraphEdge, GraphGroup).
 *
 * The algorithm:
 * 1. For each business object:
 *    - If it exists in oldMap, call update()
 *    - If it doesn't exist, call create()
 *    - Add the result to newMap (unless it's null)
 * 2. Remove the processed business objects from oldMap
 * 3. Call remove() for any remaining items in oldMap (they no longer exist in the business model)
 *
 * @returns A new map containing the updated graph items
 */
export const diffUpdateGraph = <T extends GraphItem, Y extends IModelItem>({
  oldMap,
  businessObjects,
  create,
  update,
  remove,
}: DiffUpdateGraphArgs<T, Y>): Map<string, Y> => {
  const newMap = new Map(
    businessObjects
      .map((businessObject): [string, Y | null] => [
        businessObject.id,
        // Add new items, update existing items
        (oldMap.has(businessObject.id) ? update : create)(businessObject),
      ])
      .filter(filterNullValues)
  );
  businessObjects.forEach(businessObject => oldMap.delete(businessObject.id));

  // Delete old items
  Array.from(oldMap.values()).forEach(graphItem => remove(graphItem, oldMap));
  return newMap;
};
